// Ice surface material — the rink's whole 2D look in one TSL shader.
//
// Markings are analytic SDFs evaluated in sim coordinates, so they are
// resolution-independent (crisp at any zoom) and live: every width, color,
// and intensity is a uniform written by the style JSON / material lab.
// Marking POSITIONS come from sim tuning and `faceoffSpotInto` at build
// time — the renderer cannot drift from the rules.
//
// Coordinate frame: the ice geometry bakes its flat rotation, so
// positionLocal = (simY, 0, -simX). simX = length axis (±30, away goal
// +simX), simY = width axis (±15).
//
// Recipe per marking family: a wide soft glow
// under a crisp core, both emissive so the bloom pass amplifies them.

import { faceoffSpotInto, tuning } from "@arcade-hockey/sim";
import {
  clamp,
  float,
  normalView,
  positionLocal,
  positionViewDirection,
  smoothstep,
  uniform,
  vec2,
} from "three/tsl";
import { Color, MeshStandardNodeMaterial } from "three/webgpu";
import type { IceStyle, LineFamilyStyle } from "../style/style-schema.js";

// Faceoff-circle radii are presentation constants (the sim has no circle
// mechanic); dot/crease geometry tracks the sim.
const CENTER_CIRCLE_R = 4;
const END_CIRCLE_R = 3.5;
const CENTER_DOT_R = 0.4;
const END_DOT = { halo: 0.55, core: 0.3 };
const NZ_DOT = { halo: 0.6, core: 0.34 };
const CREASE_R = 1.8;

// TSL graph values are dynamically typed at build time; the graph is
// constructed once at startup, so we trade node-generic precision for
// sanity here. Uniform records keep full types via inference below.
// biome-ignore lint/suspicious/noExplicitAny: TSL graph-building values
type AnyNode = any;

function lineFamilyUniforms(style: LineFamilyStyle) {
  return {
    color: uniform(new Color(style.color)),
    coreWidth: uniform(style.coreWidth),
    coreIntensity: uniform(style.coreIntensity),
    glowWidth: uniform(style.glowWidth),
    glowIntensity: uniform(style.glowIntensity),
  };
}

type LineFamilyUniforms = ReturnType<typeof lineFamilyUniforms>;

function buildIceUniforms(style: IceStyle) {
  return {
    baseColor: uniform(new Color(style.baseColor)),
    lineSoftness: uniform(style.lineSoftness),
    center: lineFamilyUniforms(style.center),
    blue: lineFamilyUniforms(style.blue),
    goalLine: lineFamilyUniforms(style.goalLine),
    circleRingWidth: uniform(style.circles.ringWidth),
    circleCoreIntensity: uniform(style.circles.coreIntensity),
    circleGlowWidth: uniform(style.circles.glowWidth),
    circleGlowIntensity: uniform(style.circles.glowIntensity),
    dotCoreIntensity: uniform(style.dots.coreIntensity),
    dotHaloIntensity: uniform(style.dots.haloIntensity),
    creaseFillIntensity: uniform(style.crease.fillIntensity),
    creaseRingIntensity: uniform(style.crease.ringIntensity),
    sparkleDensity: uniform(style.sparkleDensity),
    sparkleIntensity: uniform(style.sparkleIntensity),
    sheenIntensity: uniform(style.sheenIntensity),
    reflectIntensity: uniform(style.reflectIntensity),
    reflectFresnelPower: uniform(style.reflectFresnelPower),
    /** FX: rink-wide marking pulse (goal flash). 0 at rest. */
    rinkPulse: uniform(0),
  };
}

export type IceUniforms = ReturnType<typeof buildIceUniforms>;

/** Write a style JSON payload into live uniforms (lab sliders, JSON HMR). */
export function applyIceStyle(u: IceUniforms, s: IceStyle): void {
  u.baseColor.value.set(s.baseColor);
  u.lineSoftness.value = s.lineSoftness;
  for (const fam of ["center", "blue", "goalLine"] as const) {
    u[fam].color.value.set(s[fam].color);
    u[fam].coreWidth.value = s[fam].coreWidth;
    u[fam].coreIntensity.value = s[fam].coreIntensity;
    u[fam].glowWidth.value = s[fam].glowWidth;
    u[fam].glowIntensity.value = s[fam].glowIntensity;
  }
  u.circleRingWidth.value = s.circles.ringWidth;
  u.circleCoreIntensity.value = s.circles.coreIntensity;
  u.circleGlowWidth.value = s.circles.glowWidth;
  u.circleGlowIntensity.value = s.circles.glowIntensity;
  u.dotCoreIntensity.value = s.dots.coreIntensity;
  u.dotHaloIntensity.value = s.dots.haloIntensity;
  u.creaseFillIntensity.value = s.crease.fillIntensity;
  u.creaseRingIntensity.value = s.crease.ringIntensity;
  u.sparkleDensity.value = s.sparkleDensity;
  u.sparkleIntensity.value = s.sparkleIntensity;
  u.sheenIntensity.value = s.sheenIntensity;
  u.reflectIntensity.value = s.reflectIntensity;
  u.reflectFresnelPower.value = s.reflectFresnelPower;
}

export function createIceMaterial(
  style: IceStyle,
  reflection: AnyNode | null,
): { material: MeshStandardNodeMaterial; u: IceUniforms } {
  const u = buildIceUniforms(style);

  // --- Coordinates -------------------------------------------------------
  const simX = positionLocal.z.negate();
  const simY = positionLocal.x;
  const absX = simX.abs();
  const absY = simY.abs();
  const aa = u.lineSoftness;

  // Geometry anchors — sim truth.
  const blueLineX = tuning.positioning.zones.blueLineX;
  const goalLineX = tuning.rink.length / 2 - tuning.rink.goalLineOffset;
  const spot = { x: 0, y: 0 };
  faceoffSpotInto(spot, "AwayZoneLeft");
  const endX = Math.abs(spot.x);
  const endY = Math.abs(spot.y);
  faceoffSpotInto(spot, "NeutralAwayLeft");
  const nzX = Math.abs(spot.x);
  const nzY = Math.abs(spot.y);

  // --- SDF helpers (build-time JS; unrolled into the shader) -------------
  /** Crisp band: 1 inside |d| < halfWidth, antialiased by `aa`. */
  const core = (d: AnyNode, width: AnyNode): AnyNode =>
    smoothstep(width.mul(0.5).add(aa), width.mul(0.5).sub(aa), d);
  /** Soft quadratic falloff over `width`. */
  const glow = (d: AnyNode, width: AnyNode): AnyNode => {
    const t = clamp(d.div(width), 0, 1).oneMinus();
    return t.mul(t);
  };
  /** Wide+crisp line family contribution. */
  const lineFamily = (d: AnyNode, fam: LineFamilyUniforms): AnyNode =>
    fam.color.mul(
      core(d, fam.coreWidth)
        .mul(fam.coreIntensity)
        .add(glow(d, fam.glowWidth).mul(fam.glowIntensity)),
    );
  /** Filled disc mask. */
  const disc = (d: AnyNode, r: number): AnyNode => smoothstep(r + 0.02, r - 0.02, d);

  // --- Lines (fold absX: each family covers its symmetric pair) ----------
  let markings = lineFamily(absX, u.center);
  markings = markings.add(lineFamily(absX.sub(blueLineX).abs(), u.blue));
  markings = markings.add(lineFamily(absX.sub(goalLineX).abs(), u.goalLine));

  // --- Circles ------------------------------------------------------------
  const circleFamily = (d: AnyNode): AnyNode =>
    core(d, u.circleRingWidth)
      .mul(u.circleCoreIntensity)
      .add(glow(d, u.circleGlowWidth).mul(u.circleGlowIntensity));
  // Centre circle + faint inner ring + dot (cyan = blue-family color).
  const rCenter = vec2(simY, simX).length();
  markings = markings.add(u.blue.color.mul(circleFamily(rCenter.sub(CENTER_CIRCLE_R).abs())));
  markings = markings.add(
    u.blue.color.mul(core(rCenter.sub(CENTER_CIRCLE_R * 0.45).abs(), u.circleRingWidth)).mul(0.3),
  );
  markings = markings.add(u.blue.color.mul(disc(rCenter, CENTER_DOT_R)).mul(u.dotCoreIntensity));
  // End-zone circles + dots at (±endY, ±endX), folded onto one quadrant.
  const dEnd = vec2(absY.sub(endY), absX.sub(endX)).length();
  markings = markings.add(u.center.color.mul(circleFamily(dEnd.sub(END_CIRCLE_R).abs())));
  markings = markings.add(
    u.center.color.mul(
      disc(dEnd, END_DOT.core)
        .mul(u.dotCoreIntensity)
        .add(disc(dEnd, END_DOT.halo).mul(u.dotHaloIntensity)),
    ),
  );
  // Neutral-zone dots at (±nzY, ±nzX).
  const dNz = vec2(absY.sub(nzY), absX.sub(nzX)).length();
  markings = markings.add(
    u.center.color.mul(
      disc(dNz, NZ_DOT.core)
        .mul(u.dotCoreIntensity)
        .add(disc(dNz, NZ_DOT.halo).mul(u.dotHaloIntensity)),
    ),
  );

  // --- Creases (half-disc facing centre ice from each goal line) ---------
  const dCrease = vec2(simY, absX.sub(goalLineX)).length();
  const inwardSide = smoothstep(0.0, 0.05, float(goalLineX).sub(absX));
  const creaseDisc = disc(dCrease, CREASE_R).mul(inwardSide);
  const creaseRing = core(dCrease.sub(CREASE_R).abs(), u.circleRingWidth).mul(inwardSide);
  markings = markings.add(
    u.blue.color.mul(
      creaseDisc.mul(u.creaseFillIntensity).add(creaseRing.mul(u.creaseRingIntensity)),
    ),
  );

  // Goal-flash pulse lifts every marking together.
  markings = markings.mul(u.rinkPulse.add(1));

  // --- Surface life: sparkle glints + sheen bands -------------------------
  const cell = vec2(simY, simX).mul(u.sparkleDensity).floor();
  const h = cell.dot(vec2(127.1, 311.7)).sin().mul(43758.5453).fract();
  const sparkle = h.pow(180).mul(u.sparkleIntensity);
  const sheen = simX.mul(0.45).sin().mul(0.5).add(0.5).mul(u.sheenIntensity);

  // --- Reflection (planar reflector node mixed in, fresnel-weighted) ------
  let emissive = markings.add(sparkle);
  if (reflection !== null) {
    const fres = normalView.dot(positionViewDirection).abs().oneMinus().pow(u.reflectFresnelPower);
    emissive = emissive.add(reflection.rgb.mul(u.reflectIntensity).mul(fres.add(0.15)));
  }

  const material = new MeshStandardNodeMaterial({ roughness: 0.85, metalness: 0.0 });
  material.colorNode = u.baseColor.add(sheen);
  material.emissiveNode = emissive;
  return { material, u };
}
